import React, { useState, useEffect } from 'react';
import { 
  Modal, 
  Button, 
  Form, 
  Input, 
  Select, 
  Switch, 
  DatePicker, 
  Table, 
  Space, 
  message, 
  Typography, 
  Tabs, 
  Card, 
  Tag, 
  Tooltip,
  Popconfirm,
  Divider,
  Alert
} from 'antd';
import { 
  LinkOutlined, 
  CopyOutlined, 
  EyeOutlined, 
  StopOutlined, 
  PlusOutlined,
  ReloadOutlined,
  BarChartOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import axios from 'axios';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

const SurveyLinkManagementModal = ({ visible, patient, onClose, onLinkGenerated }) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [surveyLinks, setSurveyLinks] = useState([]);
  const [activeTab, setActiveTab] = useState('generate');
  const [selectedLink, setSelectedLink] = useState(null);
  const [analyticsVisible, setAnalyticsVisible] = useState(false);

  useEffect(() => {
    if (visible && patient?._id) {
      fetchSurveyLinks();
    }
  }, [visible, patient]);

  const fetchSurveyLinks = async () => {
    try {
      const response = await axios.get(`/patients/${patient._id}/survey-link`);
      if (response.data.status === 'success') {
        setSurveyLinks(response.data.data.surveyLinks);
      }
    } catch (error) {
      console.error('Error fetching survey links:', error);
      message.error('Failed to fetch survey links');
    }
  };

  const handleRefresh = async () => {
    try {
      setLoading(true);
      await fetchSurveyLinks();
      message.success('Survey links refreshed successfully');
    } catch (error) {
      console.error('Error refreshing survey links:', error);
      message.error('Failed to refresh survey links');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateLink = async (values) => {
    setLoading(true);
    try {
      const linkData = {
        expiryDays: values.expiryDays || 30,
        maxUses: values.maxUses || null,
        language: values.language || 'vi',
        allowMultipleSubmissions: values.allowMultipleSubmissions || false,
        requirePatientInfo: values.requirePatientInfo || false
      };

      const response = await axios.post(
        `/patients/${patient._id}/survey-link`,
        linkData
      );

      if (response.data.status === 'success') {
        const newLink = response.data.data.surveyLink;
        setSurveyLinks(prev => [newLink, ...prev]);
        onLinkGenerated(newLink);
        form.resetFields();
        setActiveTab('manage');
        message.success(t('healthSurvey.linkGeneratedSuccess'));
      }
    } catch (error) {
      console.error('Error generating survey link:', error);
      message.error(error.response?.data?.message || t('healthSurvey.linkGenerationFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = (url) => {
    navigator.clipboard.writeText(url).then(() => {
      message.success(t('healthSurvey.linkCopied'));
    }).catch(() => {
      message.error(t('healthSurvey.linkCopyFailed'));
    });
  };

  const handleDeactivateLink = async (linkId) => {
    try {
      const response = await axios.patch(
        `/patients/${patient._id}/survey-link/${linkId}/deactivate`,
        { reason: 'Manually deactivated by healthcare provider' }
      );

      if (response.data.status === 'success') {
        setSurveyLinks(prev => 
          prev.map(link => 
            link.id === linkId 
              ? { ...link, isActive: false, deactivatedAt: new Date() }
              : link
          )
        );
        message.success(t('healthSurvey.linkDeactivated'));
      }
    } catch (error) {
      console.error('Error deactivating survey link:', error);
      message.error(t('healthSurvey.linkDeactivationFailed'));
    }
  };

  const getStatusTag = (link) => {
    if (!link.isActive) return <Tag color="red">Deactivated</Tag>;
    if (link.isExpired) return <Tag color="orange">Expired</Tag>;
    if (link.isCompleted && !link.settings.allowMultipleSubmissions) return <Tag color="green">Completed</Tag>;
    if (link.maxUses && link.currentUses >= link.maxUses) return <Tag color="purple">Usage Limit Reached</Tag>;
    return <Tag color="blue">Active</Tag>;
  };

  const columns = [
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => moment(date).format('DD/MM/YYYY HH:mm'),
      sorter: (a, b) => moment(a.createdAt).unix() - moment(b.createdAt).unix(),
    },
    {
      title: 'Expires',
      dataIndex: 'expiresAt',
      key: 'expiresAt',
      render: (date) => moment(date).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => getStatusTag(record),
    },
    {
      title: 'Usage',
      key: 'usage',
      render: (_, record) => (
        <span>
          {record.currentUses} / {record.maxUses || '∞'}
          {record.accessCount > 0 && (
            <Text type="secondary" style={{ marginLeft: 8 }}>
              ({record.accessCount} access{record.accessCount !== 1 ? 'es' : ''})
            </Text>
          )}
        </span>
      ),
    },
    {
      title: 'Language',
      dataIndex: ['settings', 'language'],
      key: 'language',
      render: (lang) => lang === 'vi' ? 'Vietnamese' : 'English',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Tooltip title="Copy Link">
            <Button
              icon={<CopyOutlined />}
              size="small"
              onClick={() => handleCopyLink(record.url)}
            />
          </Tooltip>
          <Tooltip title="View Analytics">
            <Button
              icon={<BarChartOutlined />}
              size="small"
              onClick={() => {
                setSelectedLink(record);
                setAnalyticsVisible(true);
              }}
            />
          </Tooltip>
          {record.isActive && (
            <Popconfirm
              title="Are you sure you want to deactivate this link?"
              onConfirm={() => handleDeactivateLink(record.id)}
              okText="Yes"
              cancelText="No"
            >
              <Tooltip title="Deactivate Link">
                <Button
                  icon={<StopOutlined />}
                  size="small"
                  danger
                />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  const renderGenerateTab = () => (
    <div style={{ padding: '16px 0' }}>
      <Alert
        message="Generate Survey Link"
        description="Create a secure, time-limited link that patients can use to complete their health survey without logging into Care360."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />
      
      <Form
        form={form}
        layout="vertical"
        onFinish={handleGenerateLink}
        initialValues={{
          expiryDays: 30,
          language: 'vi',
          allowMultipleSubmissions: false,
          requirePatientInfo: false
        }}
      >
        <Form.Item
          name="expiryDays"
          label="Link Expiry (Days)"
          rules={[{ required: true, message: 'Please specify expiry days' }]}
        >
          <Select>
            <Option value={7}>7 days</Option>
            <Option value={14}>14 days</Option>
            <Option value={30}>30 days (Recommended)</Option>
            <Option value={60}>60 days</Option>
            <Option value={90}>90 days</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="maxUses"
          label="Maximum Uses (Optional)"
          help="Leave empty for unlimited uses"
        >
          <Select allowClear placeholder="Unlimited">
            <Option value={1}>1 use (Single-use)</Option>
            <Option value={3}>3 uses</Option>
            <Option value={5}>5 uses</Option>
            <Option value={10}>10 uses</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="language"
          label="Default Language"
          rules={[{ required: true, message: 'Please select language' }]}
        >
          <Select>
            <Option value="vi">Vietnamese</Option>
            <Option value="en">English</Option>
          </Select>
        </Form.Item>

        <Form.Item
          name="allowMultipleSubmissions"
          label="Allow Multiple Submissions"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Form.Item
          name="requirePatientInfo"
          label="Require Patient Information Verification"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            icon={<PlusOutlined />}
            size="large"
            block
          >
            Generate Survey Link
          </Button>
        </Form.Item>
      </Form>
    </div>
  );

  const renderManageTab = () => (
    <div style={{ padding: '16px 0' }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={5} style={{ margin: 0 }}>Survey Links for {patient?.firstName} {patient?.lastName}</Title>
        <Button
          icon={<ReloadOutlined />}
          onClick={handleRefresh}
          loading={loading}
          size="small"
        >
          Refresh
        </Button>
      </div>
      
      <Table
        columns={columns}
        dataSource={surveyLinks}
        rowKey="id"
        size="small"
        pagination={{ pageSize: 5 }}
        scroll={{ x: 800 }}
      />
    </div>
  );

  const renderAnalyticsModal = () => (
    <Modal
      title={
        <Space>
          <BarChartOutlined />
          <span>Survey Analytics - {selectedLink?.description}</span>
        </Space>
      }
      open={analyticsVisible}
      onCancel={() => {
        setAnalyticsVisible(false);
        setSelectedLink(null);
      }}
      footer={[
        <Button key="close" onClick={() => {
          setAnalyticsVisible(false);
          setSelectedLink(null);
        }}>
          Close
        </Button>
      ]}
      width={800}
    >
      <div style={{ padding: '16px 0' }}>
        {selectedLink && (
          <>
            <Card size="small" style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <Text strong>Survey Link URL:</Text>
                  <br />
                  <Text code copyable>{selectedLink.url}</Text>
                </div>
                <div>
                  <Text strong>Status: </Text>
                  <Tag color={selectedLink.isActive ? 'green' : 'red'}>
                    {selectedLink.isActive ? 'Active' : 'Inactive'}
                  </Tag>
                </div>
              </div>
            </Card>

            <Card size="small" style={{ marginBottom: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                <div>
                  <Text strong>Created:</Text>
                  <br />
                  <Text>{moment(selectedLink.createdAt).format('YYYY-MM-DD HH:mm')}</Text>
                </div>
                <div>
                  <Text strong>Expires:</Text>
                  <br />
                  <Text>{moment(selectedLink.expiresAt).format('YYYY-MM-DD HH:mm')}</Text>
                </div>
                <div>
                  <Text strong>Completion Status:</Text>
                  <br />
                  <Tag color={selectedLink.isCompleted ? 'blue' : 'orange'}>
                    {selectedLink.isCompleted ? 'Completed' : 'Pending'}
                  </Tag>
                </div>
                <div>
                  <Text strong>Accessed:</Text>
                  <br />
                  <Text>{selectedLink.lastAccessed ? moment(selectedLink.lastAccessed).format('YYYY-MM-DD HH:mm') : 'Never'}</Text>
                </div>
              </div>
            </Card>

            {selectedLink.isCompleted && selectedLink.completedAt && (
              <Card size="small" style={{ marginBottom: 16 }}>
                <Text strong>Survey Completed:</Text>
                <br />
                <Text>{moment(selectedLink.completedAt).format('YYYY-MM-DD HH:mm')}</Text>
                <br />
                <Text type="secondary">Survey responses have been recorded and are available in the patient's health record.</Text>
              </Card>
            )}

            <Alert
              message="Survey Analytics"
              description="This panel shows basic usage statistics for the survey link. Detailed survey responses are integrated into the patient's health record and can be viewed in the patient's flowsheet and health snapshot."
              type="info"
              showIcon
            />
          </>
        )}
      </div>
    </Modal>
  );

  return (
    <Modal
      title={
        <Space>
          <LinkOutlined />
          <span>Health Survey Links - {patient?.firstName} {patient?.lastName}</span>
        </Space>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1000}
      destroyOnClose
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Generate New Link" key="generate">
          {renderGenerateTab()}
        </TabPane>
        <TabPane tab="Manage Links" key="manage">
          {renderManageTab()}
        </TabPane>
      </Tabs>
      {renderAnalyticsModal()}
    </Modal>
  );
};

export default SurveyLinkManagementModal;
